
import React, { useState } from 'react';
import { useStandUp } from '../contexts/StandUpContext';
import TaskCard from './TaskCard';
import { CheckCircle2, Coffee, Loader2, Search } from 'lucide-react';
import SelectedTeamsBar from './SelectedTeamsBar';
import { BaseTask } from '../types';

const EngineerView: React.FC<{ isTray?: boolean }> = ({ isTray = false }) => {
    const { tasks, user, isTaskBlocked, updateTaskStatus, viewMode } = useStandUp();
    const [searchQuery, setSearchQuery] = useState('');

    if (!user) return null;

    const myTasks = tasks.filter(t => t.assigneeId === user.uid);
    const activeTasks = myTasks.filter(t => t.status !== 'done');
    const doneTasks = myTasks.filter(t => t.status === 'done');

    // Filter by search query
    const filterBySearch = (list: any[]) => {
        if (!searchQuery.trim()) return list;
        return list.filter(t => 
            t.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
            (t.taskKey && t.taskKey.toLowerCase().includes(searchQuery.toLowerCase()))
        );
    };

    const filteredActive = filterBySearch(activeTasks);
    const filteredDone = filterBySearch(doneTasks);

    // 1. Sort Done Tasks: Newest completion first
    doneTasks.sort((a, b) => (b.completedAt || b.updatedAt) - (a.completedAt || a.updatedAt));

    // 2. Sort Active logic: High importance (Deep Work/In Progress) -> Not Blocked -> Priority
    activeTasks.sort((a: BaseTask, b: BaseTask) => {
        // 1. Active status first (Deep Work > In Progress)
        const statusScore = (status: string) => {
            if (status === 'deep-work') return 3;
            if (status === 'in-progress') return 2;
            if (status === 'blocked') return -1;
            return 0;
        };
        const scoreA = statusScore(a.status);
        const scoreB = statusScore(b.status);
        if (scoreA !== scoreB) return scoreB - scoreA;

        // 2. Blocked status (Actionable non-blocked first)
        const aBlocked = isTaskBlocked(a);
        const bBlocked = isTaskBlocked(b);
        if (aBlocked && !bBlocked) return 1;
        if (!aBlocked && bBlocked) return -1;

        return 0;
    });

    const currentFocus = activeTasks.find(t => t.status === 'in-progress' || t.status === 'deep-work');
    const isCompact = viewMode === 'compact';
    const [isUpdating, setIsUpdating] = React.useState(false);

    const handleMarkDone = async (taskId: string) => {
        setIsUpdating(true);
        try {
            await updateTaskStatus(taskId, 'done');
        } finally {
            setIsUpdating(false);
        }
    };

    // --- COMPACT VIEW (Minimalist) ---
    if (isCompact) {
        return (
            <div className="p-3 pb-24 h-full overflow-y-auto flex flex-col gap-3 scrollbar-hide">
                {activeTasks.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-40 text-gray-400 text-center">
                        <Coffee size={24} className="mb-2 opacity-50" />
                        <span className="text-xs">No active tasks</span>
                    </div>
                ) : (
                    activeTasks.map(task => (
                        <TaskCard key={task.id} task={task} />
                    ))
                )}
            </div>
        );
    }

    // --- STANDARD FULL VIEW ---
    return (
                <div className="h-full overflow-y-auto bg-gray-50 dark:bg-gray-800" style={{ scrollbarGutter: 'stable' }}>
                    {/* Sticky Header - Hidden in Tray because Tray has its own integrated header */}
                    {!isTray && (
                        <div className="sticky top-0 z-30 bg-gray-50/80 dark:bg-gray-800/80 backdrop-blur-md px-6 py-4 flex flex-col gap-4 border-b border-gray-200 dark:border-gray-800 shadow-sm">
                            <SelectedTeamsBar />
        
                            {/* Header / Current Status */}
                            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 shrink-0">
                                <div className="flex flex-col md:flex-row md:items-center gap-4 md:gap-8">
                                    <div>
                                        <h1 className="text-2xl font-bold text-gray-900 dark:text-white text-nowrap">My Focus</h1>
                                        <p className="text-sm text-gray-500">Keep it green.</p>
                                    </div>

                                    {/* SEARCH BAR */}
                                    <div className="relative group min-w-[200px] md:min-w-[280px]">
                                        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 group-focus-within:text-brand-500 transition-colors" />
                                        <input 
                                            type="text"
                                            placeholder="Search tasks or IDs..."
                                            value={searchQuery}
                                            onChange={(e) => setSearchQuery(e.target.value)}
                                            className="w-full bg-gray-100 dark:bg-gray-800/50 border border-transparent focus:border-brand-500/30 focus:ring-2 ring-brand-500/10 rounded-xl pl-10 pr-4 py-2 text-sm outline-none transition-all dark:text-white"
                                        />
                                    </div>
                                </div>
        
                                {/* Quick Status Toggle */}
                                {currentFocus ? (
                                    <div className="bg-blue-50 dark:bg-gray-800/80 px-4 py-2 rounded-lg border border-blue-100 dark:border-blue-800 flex items-center justify-between md:justify-start gap-3 shadow-sm w-full md:w-auto">
                                        <div className="text-sm">
                                            <span className="opacity-70 text-xs uppercase font-bold tracking-wider text-nowrap dark:text-white">Working On:</span>
                                            <div className="font-semibold max-w-[180px] md:max-w-[200px] truncate dark:text-white">{currentFocus.title}</div>
                                        </div>
                                        <button
                                            onClick={() => handleMarkDone(currentFocus.id)}
                                            disabled={isUpdating}
                                            className="bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-1.5 rounded-lg text-xs font-bold flex items-center gap-1 transition-transform active:scale-95 disabled:opacity-50 shrink-0"
                                        >
                                            {isUpdating ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
                                            Mark Done
                                        </button>
                                    </div>
                                ) : (
                                    <div className="bg-emerald-50 dark:bg-emerald-900/20 px-4 py-2 rounded-lg border border-emerald-100 dark:border-emerald-800 flex items-center gap-2 text-emerald-700 dark:text-emerald-300 w-full md:w-auto">
                                        <Coffee size={18} />
                                        <span className="text-sm font-medium">All clear! Select a task to start.</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
        
                    <div className={`p-6 pb-24 flex flex-col gap-8 ${isTray ? 'pt-2' : ''}`}>
                <div className="grid md:grid-cols-2 gap-8">
                    {/* Column 1: Actionable */}
                    <div className="flex flex-col gap-4">
                        <h2 className="font-bold text-gray-500 dark:text-gray-400 text-sm uppercase tracking-wider">Up Next</h2>
                        {filteredActive.length === 0 && (
                            <div className="text-gray-400 text-sm italic border-2 border-dashed border-gray-200 dark:border-gray-700 rounded-lg p-4 text-center">
                                {searchQuery ? "No matching active tasks." : "Nothing on your plate."}
                            </div>
                        )}
                        {filteredActive.map(task => (
                            <TaskCard key={task.id} task={task} />
                        ))}
                    </div>

                    {/* Column 2: Activity & Done */}
                    <div className="flex flex-col gap-4">
                        <h2 className="font-bold text-gray-500 dark:text-gray-400 text-sm uppercase tracking-wider">Recently Completed</h2>
                        <div className="opacity-60 transition-all flex flex-col gap-2">
                            {filteredDone.length === 0 && (
                                <div className="text-gray-400 text-xs italic">
                                    {searchQuery ? "No matching completed tasks." : "No completed tasks yet."}
                                </div>
                            )}
                            {filteredDone.map(task => (
                                <TaskCard key={task.id} task={task} compact />
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default EngineerView;
